import React, { useMemo, useRef, useState } from "react";
import { internalsSymbol, useStore, type ReactFlowState } from "reactflow";

import type { DrawnCursor, ViewTransform } from "../../providers/collab/cursors";
import type {
  ScenarioBox,
  ScenarioCanvasView,
  ScenarioFrame,
  StandInEnd,
} from "../../utils/scenarios/scenarioCanvasView";
import styles from "./ScenarioLayers.module.css";

/** A collapsed scenario's box: its size, and the height of each port's row. */
export const BOX_WIDTH = 260;
const BOX_HEADER = 40;
const BOX_SECTION = 22;
const BOX_ROW = 24;
const BOX_PAD = 8;
/** Room a frame leaves around its scenario's nodes. */
const FRAME_PAD = 18;
/** How far above its frame a frame's header sits. */
const FRAME_HEADER_OFFSET = 28;
/** Room above a scenario's top node that its frame and header take. */
export const FRAME_HEADER_ROOM = FRAME_PAD + FRAME_HEADER_OFFSET;

export interface BoxLayout {
  width: number;
  height: number;
  /** Section titles and rows, as offsets from the box's top. */
  contextTitle?: number;
  outcomeTitle?: number;
  context: Map<string, number>;
  outcomes: Map<string, number>;
}

/**
 * Where a box's rows go. A stand-in edge ends at its row's port, so the box
 * and the edges read this one layout.
 */
export function boxLayout(box: ScenarioBox): BoxLayout {
  let y = BOX_HEADER;
  const layout: BoxLayout = { width: BOX_WIDTH, height: 0, context: new Map(), outcomes: new Map() };
  if (box.parts.context.length > 0) {
    layout.contextTitle = y;
    y += BOX_SECTION;
    for (const id of box.parts.context) {
      layout.context.set(id, y);
      y += BOX_ROW;
    }
  }
  if (box.parts.outcomes.length > 0) {
    layout.outcomeTitle = y;
    y += BOX_SECTION;
    for (const id of box.parts.outcomes) {
      layout.outcomes.set(id, y);
      y += BOX_ROW;
    }
  }
  layout.height = y + BOX_PAD;
  return layout;
}

type Geometry = { x: number; y: number; width: number; height: number; internal: any };

const transformOf = (s: ReactFlowState) => s.transform;
const internalsOf = (s: ReactFlowState) => s.nodeInternals;

function geometryOf(internals: ReactFlowState["nodeInternals"], id: string): Geometry | null {
  const internal: any = internals.get(id);
  if (!internal) return null;
  const at = internal.positionAbsolute ?? internal.position;
  return { x: at.x, y: at.y, width: internal.width ?? 0, height: internal.height ?? 0, internal };
}

/** The point a node's handle sits at, or the middle of the side a handle of that kind sits on. */
function handlePoint(geo: Geometry, kind: "source" | "target", handle?: string | null) {
  const bounds = geo.internal?.[internalsSymbol]?.handleBounds?.[kind] as
    | Array<{ id?: string | null; x: number; y: number; width: number; height: number }>
    | undefined;
  const found = bounds?.find((b) => (b.id ?? null) === (handle ?? null)) ?? bounds?.[0];
  if (found) return { x: geo.x + found.x + found.width / 2, y: geo.y + found.y + found.height / 2 };
  return { x: kind === "source" ? geo.x + geo.width : geo.x, y: geo.y + geo.height / 2 };
}

function curve(a: { x: number; y: number }, b: { x: number; y: number }) {
  const bend = Math.max(40, Math.abs(b.x - a.x) / 2);
  return `M ${a.x} ${a.y} C ${a.x + bend} ${a.y}, ${b.x - bend} ${b.y}, ${b.x} ${b.y}`;
}

export interface ScenarioLayersProps {
  view: ScenarioCanvasView<any, any>;
  labelOf: (id: string) => string;
  statusOf: (id: string) => { text: string; tone: "done" | "error" | "stale" | "none" };
  onExpand: (id: string) => void;
  /** Absent on the dashboard: a frame's header then has no Collapse. */
  onCollapse?: (id: string) => void;
  onMoveBox: (id: string, at: { x: number; y: number }) => void;
  /** False in a read-only view: boxes stay where they are. */
  editable: boolean;
}

/**
 * Draws what `scenarioCanvasView` adds to the canvas (#662) beside React Flow,
 * in its coordinates: frames and stand-in edges behind the nodes, boxes and the
 * frames' headers in front. None of it is in React Flow's store.
 */
export function ScenarioLayers({ view, labelOf, statusOf, onExpand, onCollapse, onMoveBox, editable }: ScenarioLayersProps) {
  const [tx, ty, zoom] = useStore(transformOf);
  const internals = useStore(internalsOf);
  const [drag, setDrag] = useState<{ id: string; dx: number; dy: number } | null>(null);
  const dragStart = useRef<{ id: string; clientX: number; clientY: number; moved: boolean } | null>(null);

  const boxes = useMemo(
    () =>
      view.boxes.map((box) => {
        const moved = drag && drag.id === box.scenario.id;
        return { box, x: box.x + (moved ? drag.dx : 0), y: box.y + (moved ? drag.dy : 0), layout: boxLayout(box) };
      }),
    [view.boxes, drag],
  );

  const frames = view.frames
    .map(({ scenario, members }) => {
      const geos = members.map((id) => geometryOf(internals, id)).filter((g): g is Geometry => !!g && g.width > 0);
      if (geos.length === 0) return null;
      const left = Math.min(...geos.map((g) => g.x)) - FRAME_PAD;
      const top = Math.min(...geos.map((g) => g.y)) - FRAME_PAD;
      const right = Math.max(...geos.map((g) => g.x + g.width)) + FRAME_PAD;
      const bottom = Math.max(...geos.map((g) => g.y + g.height)) + FRAME_PAD;
      return { scenario, left, top, width: right - left, height: bottom - top };
    })
    .filter((f): f is NonNullable<typeof f> => !!f);

  const endPoint = (end: StandInEnd, kind: "source" | "target") => {
    if ("node" in end) {
      const geo = geometryOf(internals, end.node);
      return geo && geo.width > 0 ? handlePoint(geo, kind, end.handle) : null;
    }
    const placed = boxes.find((b) => b.box.scenario.id === end.box);
    if (!placed) return null;
    const row = (end.port === "context" ? placed.layout.context : placed.layout.outcomes).get(end.of);
    if (row === undefined) return null;
    return {
      x: placed.x + (end.port === "outcome" ? placed.layout.width : 0),
      y: placed.y + row + BOX_ROW / 2,
    };
  };

  const paths = view.standIns
    .map((edge) => {
      const a = endPoint(edge.source, "source");
      const b = endPoint(edge.target, "target");
      return a && b ? { id: edge.id, d: curve(a, b) } : null;
    })
    .filter((p): p is { id: string; d: string } => !!p);

  const plane = { transform: `translate(${tx}px, ${ty}px) scale(${zoom})` };

  const startDrag = (id: string) => (event: React.PointerEvent) => {
    if (!editable || event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragStart.current = { id, clientX: event.clientX, clientY: event.clientY, moved: false };
  };
  const moveDrag = (event: React.PointerEvent) => {
    const start = dragStart.current;
    if (!start) return;
    const dx = (event.clientX - start.clientX) / zoom;
    const dy = (event.clientY - start.clientY) / zoom;
    if (!start.moved && Math.hypot(dx, dy) < 3) return;
    start.moved = true;
    setDrag({ id: start.id, dx, dy });
  };
  // Where the pointer is let go, not where the last drawn move put the box.
  const endDrag = (box: ScenarioBox) => (event: React.PointerEvent) => {
    const start = dragStart.current;
    dragStart.current = null;
    if (start?.moved && start.id === box.scenario.id) {
      onMoveBox(box.scenario.id, {
        x: box.x + (event.clientX - start.clientX) / zoom,
        y: box.y + (event.clientY - start.clientY) / zoom,
      });
    }
    setDrag(null);
  };

  if (view.boxes.length === 0 && view.frames.length === 0) return null;

  return (
    <>
      <div className={styles.behind} aria-hidden="true">
        <div className={styles.plane} style={plane}>
          {frames.map((f) => (
            <div
              key={f.scenario.id}
              className={styles.frame}
              data-scenario-frame={f.scenario.id}
              style={{
                left: f.left,
                top: f.top,
                width: f.width,
                height: f.height,
                borderColor: f.scenario.color,
                background: `${f.scenario.color}12`,
              }}
            />
          ))}
          <svg className={styles.edges}>
            {paths.map((p) => (
              <path key={p.id} d={p.d} className={styles.standIn} data-scenario-stand-in="true" />
            ))}
          </svg>
        </div>
      </div>
      <div className={styles.front}>
        <div className={styles.plane} style={plane}>
          {frames.map((f) => (
            <div
              key={f.scenario.id}
              className={styles.frameHeader}
              data-scenario-header={f.scenario.id}
              style={{ left: f.left, top: f.top - FRAME_HEADER_OFFSET }}
            >
              <span className={styles.chip} style={{ background: f.scenario.color }}>
                {f.scenario.name}
              </span>
              {onCollapse && (
                <button
                  type="button"
                  className={styles.headerButton}
                  data-testid={`scenario-collapse-${f.scenario.id}`}
                  onClick={() => onCollapse(f.scenario.id)}
                >
                  Collapse
                </button>
              )}
            </div>
          ))}
          {boxes.map(({ box, x, y, layout }) => (
            <div
              key={box.scenario.id}
              className={styles.box}
              data-scenario-box={box.scenario.id}
              data-testid={`scenario-box-${box.scenario.id}`}
              data-tooltip="Double-click to expand"
              style={{ left: x, top: y, width: layout.width, height: layout.height, borderColor: box.scenario.color }}
              onDoubleClick={() => onExpand(box.scenario.id)}
              onPointerDown={startDrag(box.scenario.id)}
              onPointerMove={moveDrag}
              onPointerUp={endDrag(box)}
            >
              <div className={styles.boxHeader} style={{ background: box.scenario.color }}>
                <span className={styles.boxName}>{box.scenario.name}</span>
                <span className={styles.boxCount}>
                  {box.parts.levers.length} node{box.parts.levers.length === 1 ? "" : "s"}
                </span>
              </div>
              {layout.contextTitle !== undefined && (
                <div className={styles.section} style={{ top: layout.contextTitle }}>
                  Fixed context
                </div>
              )}
              {[...layout.context].map(([id, top]) => (
                <div key={`c-${id}`} className={styles.row} style={{ top }}>
                  <span className={styles.portIn} style={{ borderColor: box.scenario.color }} />
                  <span className={styles.rowLabel}>{labelOf(id)}</span>
                </div>
              ))}
              {layout.outcomeTitle !== undefined && (
                <div className={styles.section} style={{ top: layout.outcomeTitle }}>
                  Outcomes
                </div>
              )}
              {[...layout.outcomes].map(([id, top]) => {
                const status = statusOf(id);
                return (
                  <div key={`o-${id}`} className={styles.row} style={{ top }} data-scenario-outcome={id}>
                    <span className={styles.rowLabel}>{labelOf(id)}</span>
                    <span className={styles.status} data-tone={status.tone}>
                      {status.text}
                    </span>
                    <span className={styles.portOut} style={{ borderColor: box.scenario.color }} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/** Attributes that tell an element of the cursors' planes apart in the page. */
export type Markers = { [attribute: `data-${string}`]: string | number | undefined };

const NO_ORIGIN = { x: 0, y: 0 };

/**
 * The pan-and-zoom plane cursors are drawn on, in the layer *className*: the
 * view's translate and zoom, from *origin*, where the canvas's top-left is in
 * that layer. It follows every pan and zoom; *children* draws on it for the
 * view it has.
 */
export function CursorPlane({
  className,
  origin = NO_ORIGIN,
  markers,
  children,
}: {
  className: string;
  origin?: { x: number; y: number };
  markers?: Markers;
  children: (view: ViewTransform) => React.ReactNode;
}) {
  const [tx, ty, zoom] = useStore(transformOf);
  const plane = { transform: `translate(${origin.x + tx}px, ${origin.y + ty}px) scale(${zoom})` };
  return (
    <div className={className} aria-hidden="true" {...markers}>
      <div className={styles.plane} style={plane}>
        {children([tx, ty, zoom])}
      </div>
    </div>
  );
}

/** The arrow of a cursor on the canvas, its tip at the cursor's point. */
export function CursorArrow() {
  return (
    <svg className={styles.cursorArrow} width="18" height="23" viewBox="-2 -2 18 23">
      <path d="M0 0 L0 16 L4.4 12.2 L7.3 18.8 L10.1 17.6 L7.3 11.2 L13 11.2 Z" />
    </svg>
  );
}

/**
 * One cursor on a plane: the arrow, its tip at (*x*, *y*), and *name*, in
 * *color*. It is scaled back by the view's *zoom*, from the arrow's tip, so it
 * has one size at any zoom and its point stays where the plane puts it.
 */
export function CanvasCursor({
  x,
  y,
  name,
  color,
  zoom,
  idle = false,
  markers,
}: {
  x: number;
  y: number;
  name: string;
  color: string;
  zoom: number;
  idle?: boolean;
  markers: Markers;
}) {
  return (
    <div
      className={styles.cursor}
      {...markers}
      data-idle={idle ? "true" : undefined}
      style={{ left: x, top: y, transform: `scale(${1 / zoom})`, color }}
    >
      <CursorArrow />
      <span className={styles.cursorName} style={{ backgroundColor: color }}>
        {name}
      </span>
    </div>
  );
}

/**
 * Everyone else's pointer, on the same pan-and-zoom plane as the scenarios'
 * layers and over everything on the canvas: each at its point of the dataflow,
 * in its person's color with their name, and the same size at any zoom.
 * A cursor that moves glides to its next point; one that rests is faded.
 */
export function ParticipantCursors({ cursors }: { cursors: readonly DrawnCursor[] }) {
  if (cursors.length === 0) return null;
  return (
    <CursorPlane className={styles.cursors}>
      {([, , zoom]) =>
        cursors.map((cursor) => (
          <CanvasCursor
            key={cursor.clientId}
            x={cursor.x}
            y={cursor.y}
            name={cursor.name}
            color={cursor.color}
            zoom={zoom}
            idle={cursor.idle}
            markers={{ "data-canvas-cursor": cursor.clientId }}
          />
        ))
      }
    </CursorPlane>
  );
}

const NO_STATUS = () => ({ text: "", tone: "none" as const });
const NOTHING = () => {};

/**
 * The dashboard's: each scenario's column of tiles framed, under a header in
 * its color (#662). No box, nothing to collapse or move.
 */
export function DashboardScenarioLayers({ frames }: { frames: ScenarioFrame[] }) {
  const view = useMemo(
    () => ({ nodes: [], edges: [], boxes: [], frames, standIns: [] }),
    [frames],
  );
  return (
    <ScenarioLayers
      view={view}
      editable={false}
      labelOf={(id) => id}
      statusOf={NO_STATUS}
      onExpand={NOTHING}
      onMoveBox={NOTHING}
    />
  );
}
